/**
 * Configuration diff utilities for hermit-μDC
 *
 * Provides functions to compare configuration snapshots and generate
 * human-readable change descriptions.
 */

import { DatacenterConfig, DatacenterNode } from '../types/configurator';

export interface ConfigChange {
  type: 'added' | 'removed' | 'modified';
  category: 'node' | 'network' | 'service' | 'security' | 'general';
  field: string;
  description: string;
  oldValue?: string;
  newValue?: string;
}

export interface ConfigSnapshot {
  id: string;
  timestamp: number;
  config: DatacenterConfig;
  label?: string;
}

/**
 * Generate a unique snapshot ID
 */
export function generateSnapshotId(): string {
  return `snap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Compare two configurations and return a list of changes
 */
export function diffConfigs(
  oldConfig: DatacenterConfig,
  newConfig: DatacenterConfig
): ConfigChange[] {
  const changes: ConfigChange[] = [];

  // Compare scenario
  if (oldConfig.scenario !== newConfig.scenario) {
    changes.push({
      type: 'modified',
      category: 'general',
      field: 'scenario',
      description: `Changed scenario from "${oldConfig.scenario}" to "${newConfig.scenario}"`,
      oldValue: oldConfig.scenario,
      newValue: newConfig.scenario,
    });
  }

  // Compare name
  if (oldConfig.name !== newConfig.name) {
    changes.push({
      type: 'modified',
      category: 'general',
      field: 'name',
      description: `Renamed from "${oldConfig.name}" to "${newConfig.name}"`,
      oldValue: oldConfig.name,
      newValue: newConfig.name,
    });
  }

  // Compare nodes
  const oldNodeIds = new Set(oldConfig.nodes.map(n => n.id));
  const newNodeIds = new Set(newConfig.nodes.map(n => n.id));

  // Find added nodes
  for (const node of newConfig.nodes) {
    if (!oldNodeIds.has(node.id)) {
      changes.push({
        type: 'added',
        category: 'node',
        field: `nodes.${node.id}`,
        description: `Added ${node.type} node "${node.hostname}" (${node.ip})`,
        newValue: node.hostname,
      });
    }
  }

  // Find removed nodes
  for (const node of oldConfig.nodes) {
    if (!newNodeIds.has(node.id)) {
      changes.push({
        type: 'removed',
        category: 'node',
        field: `nodes.${node.id}`,
        description: `Removed ${node.type} node "${node.hostname}"`,
        oldValue: node.hostname,
      });
    }
  }

  // Find modified nodes
  for (const newNode of newConfig.nodes) {
    const oldNode = oldConfig.nodes.find(n => n.id === newNode.id);
    if (oldNode) {
      const nodeChanges = diffNodes(oldNode, newNode);
      changes.push(...nodeChanges);
    }
  }

  // Compare network settings
  const networkChanges = diffNetwork(oldConfig.network, newConfig.network);
  changes.push(...networkChanges);

  // Compare security settings
  const securityChanges = diffSecurity(oldConfig.security, newConfig.security);
  changes.push(...securityChanges);

  // Compare service allocations
  const serviceChanges = diffServices(oldConfig.serviceAllocations, newConfig.serviceAllocations);
  changes.push(...serviceChanges);

  return changes;
}

/**
 * Compare two nodes and return changes
 */
function diffNodes(oldNode: DatacenterNode, newNode: DatacenterNode): ConfigChange[] {
  const changes: ConfigChange[] = [];

  if (oldNode.hostname !== newNode.hostname) {
    changes.push({
      type: 'modified',
      category: 'node',
      field: `nodes.${newNode.id}.hostname`,
      description: `Renamed node from "${oldNode.hostname}" to "${newNode.hostname}"`,
      oldValue: oldNode.hostname,
      newValue: newNode.hostname,
    });
  }

  if (oldNode.ip !== newNode.ip) {
    changes.push({
      type: 'modified',
      category: 'node',
      field: `nodes.${newNode.id}.ip`,
      description: `Changed IP of "${newNode.hostname}" from ${oldNode.ip} to ${newNode.ip}`,
      oldValue: oldNode.ip,
      newValue: newNode.ip,
    });
  }

  if (oldNode.enabled !== newNode.enabled) {
    changes.push({
      type: 'modified',
      category: 'node',
      field: `nodes.${newNode.id}.enabled`,
      description: newNode.enabled
        ? `Enabled node "${newNode.hostname}"`
        : `Disabled node "${newNode.hostname}"`,
      oldValue: String(oldNode.enabled),
      newValue: String(newNode.enabled),
    });
  }

  // Compare specs
  if (oldNode.specs.cpuCores !== newNode.specs.cpuCores) {
    changes.push({
      type: 'modified',
      category: 'node',
      field: `nodes.${newNode.id}.specs.cpuCores`,
      description: `Changed CPU cores of "${newNode.hostname}" from ${oldNode.specs.cpuCores} to ${newNode.specs.cpuCores}`,
      oldValue: String(oldNode.specs.cpuCores),
      newValue: String(newNode.specs.cpuCores),
    });
  }

  if (oldNode.specs.memoryGB !== newNode.specs.memoryGB) {
    changes.push({
      type: 'modified',
      category: 'node',
      field: `nodes.${newNode.id}.specs.memoryGB`,
      description: `Changed memory of "${newNode.hostname}" from ${oldNode.specs.memoryGB}GB to ${newNode.specs.memoryGB}GB`,
      oldValue: String(oldNode.specs.memoryGB),
      newValue: String(newNode.specs.memoryGB),
    });
  }

  return changes;
}

/**
 * Compare network configurations
 */
function diffNetwork(
  oldNet: DatacenterConfig['network'],
  newNet: DatacenterConfig['network']
): ConfigChange[] {
  const changes: ConfigChange[] = [];

  if (oldNet.subnet !== newNet.subnet) {
    changes.push({
      type: 'modified',
      category: 'network',
      field: 'network.subnet',
      description: `Changed subnet from ${oldNet.subnet} to ${newNet.subnet}`,
      oldValue: oldNet.subnet,
      newValue: newNet.subnet,
    });
  }

  if (oldNet.gateway !== newNet.gateway) {
    changes.push({
      type: 'modified',
      category: 'network',
      field: 'network.gateway',
      description: `Changed gateway from ${oldNet.gateway} to ${newNet.gateway}`,
      oldValue: oldNet.gateway,
      newValue: newNet.gateway,
    });
  }

  if (oldNet.domain !== newNet.domain) {
    changes.push({
      type: 'modified',
      category: 'network',
      field: 'network.domain',
      description: `Changed domain from ${oldNet.domain} to ${newNet.domain}`,
      oldValue: oldNet.domain,
      newValue: newNet.domain,
    });
  }

  return changes;
}

/**
 * Compare security configurations
 */
function diffSecurity(
  oldSec: DatacenterConfig['security'],
  newSec: DatacenterConfig['security']
): ConfigChange[] {
  const changes: ConfigChange[] = [];

  if (oldSec.vpnEnabled !== newSec.vpnEnabled) {
    changes.push({
      type: 'modified',
      category: 'security',
      field: 'security.vpnEnabled',
      description: newSec.vpnEnabled ? 'Enabled VPN' : 'Disabled VPN',
      oldValue: String(oldSec.vpnEnabled),
      newValue: String(newSec.vpnEnabled),
    });
  }

  if (oldSec.encryptStorage !== newSec.encryptStorage) {
    changes.push({
      type: 'modified',
      category: 'security',
      field: 'security.encryptStorage',
      description: newSec.encryptStorage ? 'Enabled storage encryption' : 'Disabled storage encryption',
      oldValue: String(oldSec.encryptStorage),
      newValue: String(newSec.encryptStorage),
    });
  }

  // Compare VPN peers
  const oldPeerIds = new Set(oldSec.peers.map(p => p.id));
  const newPeerIds = new Set(newSec.peers.map(p => p.id));

  for (const peer of newSec.peers) {
    if (!oldPeerIds.has(peer.id)) {
      changes.push({
        type: 'added',
        category: 'security',
        field: `security.peers.${peer.id}`,
        description: `Added VPN peer "${peer.name}"`,
        newValue: peer.name,
      });
    }
  }

  for (const peer of oldSec.peers) {
    if (!newPeerIds.has(peer.id)) {
      changes.push({
        type: 'removed',
        category: 'security',
        field: `security.peers.${peer.id}`,
        description: `Removed VPN peer "${peer.name}"`,
        oldValue: peer.name,
      });
    }
  }

  return changes;
}

/**
 * Compare service allocations
 */
function diffServices(
  oldServices: DatacenterConfig['serviceAllocations'],
  newServices: DatacenterConfig['serviceAllocations']
): ConfigChange[] {
  const changes: ConfigChange[] = [];

  const oldKey = (s: { serviceId: string; nodeId: string }) => `${s.serviceId}:${s.nodeId}`;
  const oldSet = new Set(oldServices.map(oldKey));
  const newSet = new Set(newServices.map(oldKey));

  for (const service of newServices) {
    const key = oldKey(service);
    if (!oldSet.has(key)) {
      changes.push({
        type: 'added',
        category: 'service',
        field: `services.${service.serviceId}`,
        description: `Allocated service "${service.serviceId}" to node`,
        newValue: service.serviceId,
      });
    }
  }

  for (const service of oldServices) {
    const key = oldKey(service);
    if (!newSet.has(key)) {
      changes.push({
        type: 'removed',
        category: 'service',
        field: `services.${service.serviceId}`,
        description: `Deallocated service "${service.serviceId}"`,
        oldValue: service.serviceId,
      });
    }
  }

  return changes;
}

/**
 * Generate a summary of changes
 */
export function summarizeChanges(changes: ConfigChange[]): string {
  if (changes.length === 0) {
    return 'No changes';
  }

  const added = changes.filter(c => c.type === 'added').length;
  const removed = changes.filter(c => c.type === 'removed').length;
  const modified = changes.filter(c => c.type === 'modified').length;

  const parts: string[] = [];
  if (added > 0) parts.push(`${added} added`);
  if (removed > 0) parts.push(`${removed} removed`);
  if (modified > 0) parts.push(`${modified} modified`);

  return parts.join(', ');
}

/**
 * Group changes by category
 */
export function groupChangesByCategory(changes: ConfigChange[]): Record<string, ConfigChange[]> {
  return changes.reduce((acc, change) => {
    if (!acc[change.category]) {
      acc[change.category] = [];
    }
    acc[change.category].push(change);
    return acc;
  }, {} as Record<string, ConfigChange[]>);
}
